import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Merge, Loader2, Search, Check, AlertTriangle, ArrowLeft } from 'lucide-react';
import type { Ticket } from '@centraoneplus/shared-types';
import { ticketService } from '@services/ticket.service';

// Merge other tickets INTO the ticket being viewed (ticket detail page). The
// current ticket is always the one kept; each picked ticket goes through the
// same POST /bulk/merge the list page's bulk Merge uses — its conversation
// moves onto this ticket and it is closed with status 'merged'.

interface MergeIntoTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The ticket being viewed — it is kept and receives the others. */
  currentTicket: Pick<Ticket, 'id' | 'ticketNumber' | 'subject'>;
  onMerge: (sourceTickets: Ticket[], note: string) => Promise<void>;
  isSubmitting?: boolean;
}

const MERGE_SEARCH_DEBOUNCE_MS = 300;
const RESULT_LIMIT = 20;

export function MergeIntoTicketModal(props: MergeIntoTicketModalProps) {
  // Mounting the dialog only while open gives every opening fresh state and,
  // with gcTime 0, a fresh search — so tickets merged a moment ago never
  // reappear from cache.
  if (!props.isOpen) return null;
  return <MergeIntoTicketDialog {...props} />;
}

function MergeIntoTicketDialog({
  onClose,
  currentTicket,
  onMerge,
  isSubmitting = false,
}: MergeIntoTicketModalProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selected, setSelected] = useState<Ticket[]>([]);
  const [note, setNote] = useState('');
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), MERGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  // Empty query = most recently updated tickets.
  const { data, isLoading: isSearching, error } = useQuery({
    queryKey: ['ticket-merge-candidates', currentTicket.id, debouncedQuery],
    queryFn: () =>
      ticketService.getTickets({
        search: debouncedQuery || undefined,
        limit: RESULT_LIMIT,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      }),
    gcTime: 0,
  });

  // The ticket being viewed and tickets already merged away are never offered.
  const results = useMemo(
    () =>
      (data?.data ?? []).filter(
        (t) => t.id !== currentTicket.id && (t.status as string) !== 'merged',
      ),
    [data, currentTicket.id],
  );

  // Escape closes (unless a merge is in flight); Tab stays inside the dialog.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isSubmitting, onClose]);

  const isSelected = (id: string) => selected.some((t) => t.id === id);
  const toggle = (ticket: Ticket) =>
    setSelected((prev) =>
      prev.some((t) => t.id === ticket.id) ? prev.filter((t) => t.id !== ticket.id) : [...prev, ticket],
    );

  const count = selected.length;
  const plural = count === 1 ? '' : 's';
  const searchError = error
    ? (error as any)?.response?.data?.message || (error as Error).message || 'Could not load tickets'
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="merge-into-modal-title"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-xl max-h-[90vh] rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3 min-w-0">
            <div className="mt-0.5 h-9 w-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
              <Merge className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h2 id="merge-into-modal-title" className="text-base font-semibold text-gray-900 dark:text-white">
                Merge tickets into #{currentTicket.ticketNumber}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                {currentTicket.subject}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50 dark:text-gray-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === 'select' ? (
          <div className="flex min-h-0 flex-1 flex-col px-6 py-4 gap-3">
            <label htmlFor="merge-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Find tickets to merge
            </label>
            <div className="relative -mt-1.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                id="merge-search"
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ticket number, subject or customer…"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 pl-9 pr-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-800 focus:border-primary-500"
              />
            </div>

            <div className="min-h-[200px] flex-1 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              {isSearching ? (
                <div className="flex items-center justify-center gap-2 p-8 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                </div>
              ) : searchError ? (
                <p role="alert" className="p-6 text-center text-sm text-red-600 dark:text-red-400">{searchError}</p>
              ) : results.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No tickets found.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {results.map((t) => {
                    const on = isSelected(t.id);
                    return (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => toggle(t)}
                          aria-pressed={on}
                          className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition ${
                            on ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <span
                            className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded border ${
                              on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            {on && <Check className="h-3 w-3" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">#{t.ticketNumber}</span>
                              <span className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-600 dark:text-gray-300">
                                {String(t.status).replace(/_/g, ' ')}
                              </span>
                            </span>
                            <span className="block truncate text-sm text-gray-900 dark:text-gray-100">{t.subject}</span>
                            {(t.contactName || t.contactEmail) && (
                              <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                {t.contactName || t.contactEmail}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
              {count > 0
                ? `${count} ticket${plural} selected — will be merged into #${currentTicket.ticketNumber}.`
                : `Select one or more tickets to merge into #${currentTicket.ticketNumber}.`}
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4" /> What happens when you merge
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-200">
                <li>
                  <strong>#{currentTicket.ticketNumber} is kept</strong> — its status, assignee, priority and fields don't change.
                </li>
                <li>
                  All conversations and messages (replies and internal notes) from the selected ticket{plural} move onto #{currentTicket.ticketNumber}, and an internal note on #{currentTicket.ticketNumber} records each merge.
                </li>
                <li>
                  The selected ticket{plural} {count === 1 ? 'is' : 'are'} closed with status <strong>Merged</strong>, stop counting as open work and link to #{currentTicket.ticketNumber}. Customer email replies to {count === 1 ? 'it' : 'them'} arrive on #{currentTicket.ticketNumber}.
                </li>
                <li>
                  Their attachments move too. Tags, history and fields stay on the merged ticket{plural} and are not copied.
                </li>
                <li>This can't be undone.</li>
              </ul>
            </div>

            <div>
              <p className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                Merging {count} ticket{plural}
              </p>
              <ul className="space-y-1">
                {selected.map((t) => (
                  <li key={t.id} className="truncate text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-gray-500 dark:text-gray-400">#{t.ticketNumber}</span> · {t.subject}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <label htmlFor="merge-note" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Merge note <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                id="merge-note"
                rows={3}
                maxLength={1000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isSubmitting}
                placeholder="e.g. Same customer, same issue reported twice"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-800 focus:border-primary-500 disabled:opacity-60"
              />
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Added to the internal merge note on #{currentTicket.ticketNumber}.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
          {step === 'confirm' ? (
            <button
              type="button"
              onClick={() => setStep('select')}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition disabled:opacity-50"
            >
              Cancel
            </button>
            {step === 'select' ? (
              <button
                type="button"
                onClick={() => setStep('confirm')}
                disabled={count === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onMerge(selected, note.trim())}
                disabled={isSubmitting || count === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Merging…
                  </>
                ) : (
                  <>
                    <Merge className="h-4 w-4" /> Merge {count} ticket{plural}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
